import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../../services/api';
import { message, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

/**
 * Markaları yükleyen ve yöneten hook
 * @param {number} pageSize - Sayfa başına gösterilecek marka sayısı
 * @param {array} restrictedBrandIds - Kısıtlı marka listesi
 * @returns {Object} Markalarla ilgili veri ve fonksiyonlar
 */
const useBrandsData = (pageSize = 15, restrictedBrandIds = null, includeInactive = false, selectedMonth = null) => {
  const [allBrands, setAllBrands] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const searchInputRef = useRef(null);
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [selectedGrowth, setSelectedGrowth] = useState(null);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [selectedGoogle, setSelectedGoogle] = useState(null);
  const [selectedSeo, setSelectedSeo] = useState(null);
  
  const [advisors, setAdvisors] = useState([]);
  const [advisorsLoading, setAdvisorsLoading] = useState(false);
  const [brandAdvisors, setBrandAdvisors] = useState([]);
  const [brandGrowthAdvisors, setBrandGrowthAdvisors] = useState([]);
  const [brandAssistantAdvisors, setBrandAssistantAdvisors] = useState([]);
  const [brandMetaAdvisors, setBrandMetaAdvisors] = useState([]);
  const [brandGoogleAdvisors, setBrandGoogleAdvisors] = useState([]);
  const [brandSeoAdvisors, setBrandSeoAdvisors] = useState([]);
  
  const [selectedStatus, setSelectedStatus] = useState(null);

  // İlk yüklemede markaları getir
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true);
        
        // API'den markaları getir - displayOrder parametresi ile sıralı getir
        const response = await api.get('/brands', {
          params: {
            limit: pageSize,
            offset: 0,
            orderBy: 'displayOrder', // displayOrder'a göre sıralama
            orderDirection: 'asc',   // Küçükten büyüğe sıralama
            includeInactive: includeInactive ? 'true' : 'false', // Pasif markaları dahil et
            date: selectedMonth && selectedMonth.isValid && selectedMonth.isValid() ? selectedMonth.format('YYYY-MM') : null // Seçili ay
          }
        });
        
        if (!response.data || !Array.isArray(response.data)) {
          throw new Error('Beklenmeyen API yanıtı');
        }
        
        // Eğer kısıtlı marka listesi varsa, sadece o markaları göster
        let brandsData = response.data;
        
        if (restrictedBrandIds && Array.isArray(restrictedBrandIds) && restrictedBrandIds.length > 0) {
          const filteredBrandsData = brandsData.filter(brand => 
            restrictedBrandIds.includes(brand.id)
          );
          
          // Kısıtlı markaları doğru sırada göster
          const orderedBrands = [];
          restrictedBrandIds.forEach(id => {
            const brandItem = filteredBrandsData.find(brand => brand.id === id);
            if (brandItem) {
              orderedBrands.push(brandItem);
            }
          });
          
          brandsData = orderedBrands;
        } else {
          // Kısıtlı marka listesi yoksa, displayOrder alanına göre sırala
          brandsData = brandsData.sort((a, b) => {
            const orderA = a.displayOrder || 0;
            const orderB = b.displayOrder || 0;
            return orderA - orderB;
          });
        }
        
        // Tüm markaları ayarla
        setAllBrands(brandsData);
        
        // İlk sayfa markalarını ayarla
        setBrands(brandsData.slice(0, pageSize));
        
        // Daha fazla marka var mı kontrol et
        setHasMore(brandsData.length > pageSize);
        
        // Markalarda kullanılan advisor listelerini oluştur
        const uniqueAdvisors = new Set();
        const uniqueGrowthAdvisors = new Set();
        const uniqueAssistantAdvisors = new Set();
        const uniqueMetaAdvisors = new Set();
        const uniqueGoogleAdvisors = new Set();
        const uniqueSeoAdvisors = new Set();
        
        const advisorsList = [];
        const growthAdvisorsList = [];
        const assistantAdvisorsList = [];
        const metaAdvisorsList = [];
        const googleAdvisorsList = [];
        const seoAdvisorsList = [];
        
        brandsData.forEach(brand => {
          // Advisor listesi
          if (brand.advisor && !uniqueAdvisors.has(brand.advisor)) {
            uniqueAdvisors.add(brand.advisor);
            advisorsList.push({
              id: advisorsList.length + 1,
              advisor_name: brand.advisor
            });
          }
          
          // Growth advisor listesi
          if (brand.growth && !uniqueGrowthAdvisors.has(brand.growth)) {
            uniqueGrowthAdvisors.add(brand.growth);
            growthAdvisorsList.push({
              id: growthAdvisorsList.length + 1,
              advisor_name: brand.growth
            });
          }
          
          // Assistant advisor listesi
          if (brand.assistant && !uniqueAssistantAdvisors.has(brand.assistant)) {
            uniqueAssistantAdvisors.add(brand.assistant);
            assistantAdvisorsList.push({
              id: assistantAdvisorsList.length + 1,
              advisor_name: brand.assistant
            });
          }
          
          // Meta advisor listesi
          if (brand.meta && !uniqueMetaAdvisors.has(brand.meta)) {
            uniqueMetaAdvisors.add(brand.meta);
            metaAdvisorsList.push({
              id: metaAdvisorsList.length + 1,
              advisor_name: brand.meta
            });
          }
          
          // Google advisor listesi
          if (brand.google && !uniqueGoogleAdvisors.has(brand.google)) {
            uniqueGoogleAdvisors.add(brand.google);
            googleAdvisorsList.push({
              id: googleAdvisorsList.length + 1,
              advisor_name: brand.google
            });
          }
          
          // SEO advisor listesi
          if (brand.seo && !uniqueSeoAdvisors.has(brand.seo)) {
            uniqueSeoAdvisors.add(brand.seo);
            seoAdvisorsList.push({
              id: seoAdvisorsList.length + 1,
              advisor_name: brand.seo
            });
          }
        });
        
        setBrandAdvisors(advisorsList);
        setBrandGrowthAdvisors(growthAdvisorsList);
        setBrandAssistantAdvisors(assistantAdvisorsList);
        setBrandMetaAdvisors(metaAdvisorsList);
        setBrandGoogleAdvisors(googleAdvisorsList);
        setBrandSeoAdvisors(seoAdvisorsList);
        
      } catch (error) {
        console.error('Markalar yüklenirken hata:', error);
        message.error('Markalar yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBrands();
  }, [pageSize, restrictedBrandIds, includeInactive, selectedMonth]);
  
  // Advisor listesini yükle
  useEffect(() => {
    const fetchAdvisors = async () => {
      try {
        setAdvisorsLoading(true);
        const response = await api.get('/advisors');
        
        // Tüm advisor listesi yerine sadece markalarda kullanılan advisorları göster
        if (brandAdvisors && brandAdvisors.length > 0) {
          setAdvisors(brandAdvisors || []);
        } else {
          // Herhangi bir marka için advisor yoksa API'den alınanları kullan
          setAdvisors(response.data || []);
        }
      } catch (error) {
        console.error('Advisor listesi yüklenirken hata:', error);
        message.error('Advisor listesi yüklenemedi');
        // Hata durumunda bile brandAdvisors varsa kullan
        if (brandAdvisors && brandAdvisors.length > 0) {
          setAdvisors(brandAdvisors);
        } else {
          setAdvisors([]);
        }
      } finally {
        setAdvisorsLoading(false);
      }
    };

    fetchAdvisors();
  }, [brandAdvisors, brandGrowthAdvisors, brandAssistantAdvisors, brandMetaAdvisors, brandGoogleAdvisors, brandSeoAdvisors]);
  
  // Arama değeri ve tüm advisor filtreleri değiştiğinde markaları filtrele
  useEffect(() => {
    const searchFilterActive = searchValue && searchValue.trim() !== '';
    const advisorFilterActive = selectedAdvisor !== null;
    const growthFilterActive = selectedGrowth !== null;
    const assistantFilterActive = selectedAssistant !== null;
    const metaFilterActive = selectedMeta !== null;
    const googleFilterActive = selectedGoogle !== null;
    const seoFilterActive = selectedSeo !== null;
    const statusFilterActive = selectedStatus !== null;
    
    const anyFilterActive = searchFilterActive || advisorFilterActive || growthFilterActive || 
                           assistantFilterActive || metaFilterActive || googleFilterActive || 
                           seoFilterActive || statusFilterActive;
    
    if (!anyFilterActive) {
      setIsFiltering(false);
      setFilteredBrands([]);
      return;
    }
    
    setIsFiltering(true);
    
    // Markaları filtrele
    let filtered = [...allBrands];
    
    // Arama filtresini uygula
    if (searchFilterActive) {
      filtered = filtered.filter(brand => 
        brand.name.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    
    // Tüm advisor filtrelerini uygula
    if (advisorFilterActive) {
      filtered = filtered.filter(brand => 
        brand.advisor === selectedAdvisor
      );
    }
    
    if (growthFilterActive) {
      filtered = filtered.filter(brand => 
        brand.growth === selectedGrowth
      );
    }
    
    if (assistantFilterActive) {
      filtered = filtered.filter(brand => 
        brand.assistant === selectedAssistant
      );
    }
    
    if (metaFilterActive) {
      filtered = filtered.filter(brand => 
        brand.meta === selectedMeta
      );
    }
    
    if (googleFilterActive) {
      filtered = filtered.filter(brand => 
        brand.google === selectedGoogle
      );
    }
    
    if (seoFilterActive) {
      filtered = filtered.filter(brand => 
        brand.seo === selectedSeo
      );
    }
    
    // Status filtresini uygula
    if (statusFilterActive) {
      filtered = filtered.filter(brand => 
        brand.status === selectedStatus
      );
    }
    
    // displayOrder'a göre sırala
    filtered = filtered.sort((a, b) => {
      const orderA = a.displayOrder || 0;
      const orderB = b.displayOrder || 0;
      return orderA - orderB;
    });
    
    setFilteredBrands(filtered);
  }, [searchValue, selectedAdvisor, selectedGrowth, selectedAssistant, selectedMeta, selectedGoogle, selectedSeo, selectedStatus, allBrands]);
  
  // Markaları sayfalama için yükle
  const loadBrandsPage = useCallback((page) => {
    if (!allBrands || allBrands.length === 0) return;
    
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    
    if (start >= allBrands.length) return;
    
    // İlgili sayfa için markaları al
    const pageBrands = allBrands.slice(start, end);
    setBrands(pageBrands);
    
    return pageBrands;
  }, [allBrands, pageSize]);
  
  // Daha fazla marka yükle
  const loadMoreBrands = useCallback(async (start, limit) => {
    if (!hasMore) return [];
    
    try {
      // Kısıtlı marka listesi varsa ve zaten tüm markaları gösteriyorsak
      if (restrictedBrandIds && allBrands.length === restrictedBrandIds.length) {
        const startIdx = start;
        const endIdx = start + limit;
        
        if (startIdx >= allBrands.length) {
          return [];
        }
        
        return allBrands.slice(startIdx, endIdx);
      }
      
      // Kısıtlı marka listesi yoksa veya daha fazla marka varsa normal şekilde devam et
      const currentShown = brands.length;
      
      if (start < currentShown) {
        // Zaten yüklü olan markalardan al
        return brands.slice(start, start + limit);
      }
      
      const moreStart = currentShown;
      const moreLimit = (start + limit) - currentShown;
      
      if (moreStart >= allBrands.length) {
        return [];
      }
      
      const moreBrands = allBrands.slice(moreStart, moreStart + moreLimit);
      
      if (moreBrands.length === 0) {
        setHasMore(false);
        return [];
      }
      
      // Markaları güncelle
      setBrands(prevBrands => [...prevBrands, ...moreBrands]);
      
      // Son sayfa mı kontrol et
      setHasMore(allBrands.length > (currentShown + moreBrands.length));
      
      return moreBrands;
    } catch (error) {
      console.error('Daha fazla marka yüklenirken hata:', error);
      message.error('Markalar yüklenirken bir hata oluştu');
      return [];
    }
  }, [brands, hasMore, allBrands, restrictedBrandIds]);
  
  // Tüm advisor filtrelerini değiştir
  const handleAdvisorChange = useCallback((value) => {
    setSelectedAdvisor(value);
  }, []);
  
  const handleGrowthChange = useCallback((value) => {
    setSelectedGrowth(value);
  }, []);
  
  const handleAssistantChange = useCallback((value) => {
    setSelectedAssistant(value);
  }, []);
  
  const handleMetaChange = useCallback((value) => {
    setSelectedMeta(value);
  }, []);
  
  const handleGoogleChange = useCallback((value) => {
    setSelectedGoogle(value);
  }, []);
  
  const handleSeoChange = useCallback((value) => {
    setSelectedSeo(value);
  }, []);
  
  // Status filtresini değiştir
  const handleStatusChange = useCallback((value) => {
    setSelectedStatus(value);
  }, []);
  
  // Arama inputu render fonksiyonu
  const renderSearchInput = () => (
    <Input
      ref={searchInputRef}
      placeholder="Marka ara..."
      prefix={<SearchOutlined />}
      value={searchValue}
      onChange={e => setSearchValue(e.target.value)}
      style={{ width: 200 }}
      allowClear
    />
  );
  
  return {
    allBrands,
    brands,
    loading,
    setLoading,
    hasMore,
    loadMoreBrands,
    searchValue,
    setSearchValue,
    filteredBrands,
    isFiltering,
    searchInputRef,
    renderSearchInput,
    loadBrandsPage,
    
    // Tüm advisor listeleri
    advisors,
    advisorsLoading,
    brandGrowthAdvisors,
    brandAssistantAdvisors,
    brandMetaAdvisors,
    brandGoogleAdvisors,
    brandSeoAdvisors,
    
    // Tüm advisor filtreleri
    selectedAdvisor,
    selectedGrowth,
    selectedAssistant,
    selectedMeta,
    selectedGoogle,
    selectedSeo,
    
    // Tüm advisor handler'lar
    handleAdvisorChange,
    handleGrowthChange,
    handleAssistantChange,
    handleMetaChange,
    handleGoogleChange,
    handleSeoChange,
    
    // Status filtresi
    selectedStatus,
    handleStatusChange
  };
};

export default useBrandsData; 